import { LegacyRef } from 'react';
import { ActionMap, GoMotoFunction } from '../../../API/GoMoto.types';

/**
 * The props object that `usePagination` takes in.
 */
export interface PaginationProps {
    /**
     * The total items that need to be paginated. Defaults to 0 when left undefinded.
     */
    totalItems?: number;
    /**
     * The initial page that should be displayed to the user. Defaults to 0 when left undefined.
     */
    initialPage?: number;
    /**
     * The initial number of elements that should display on each page. Defaults to 0 when left undefined.
     */
    initialPageSize?: number;
    /**
     * The container that contains all other elements that need to be measured and subtracted
     * from the container height to determine the actual height of space available for the grid.
     */
    containerElement: HTMLElement | null;
    /**
     * The container for the grid that has row-gap and other grid-based height styling.
     */
    gridElement: HTMLElement | null;
    /**
     * An array of all elements that will be subtracted from the height of the container to find
     * the maximum height the grid can occupy.
     */
    additionalElementsInContainer?: Array<HTMLElement | null>;
    /**
     * An array of all elements that will be included in the grid's height measurement that are not
     * the grid itself. i.e. A row of buttons.
     */
    additionalElementsInGrid?: Array<HTMLElement | null>;
}

/**
 * The return object for the `usePagination` hook.
 */
export interface PaginationReturnProps {
    /**
     * Dispatch function to set what the current page should be. If the page exceeds the maximum total pages,
     * no change will be made to the current page.
     */
    setPage: GoMotoFunction<[number], void>;
    /**
     * Dispatch function used to proceed to the next page. Should be used in the UI element that will allow
     * the user to go to the next page.
     */
    setNextPage: VoidFunction;
    /**
     * Dispatch function used to proceed to the previous page. Should be used in the UI element that will allow
     * the user to go to the previous page.
     */
    setPreviousPage: VoidFunction;
    /**
     * Dispatch function to set the number of elements that should display on each page. This overrides any height
     * calculations that have been made and forces a certain number of elements to display.
     */
    setPageSize: GoMotoFunction<[number, number], void>;
    /**
     * The current page being displayed to the user.
     */
    currentPage: number;
    /**
     * The maximum number of pages that exists based on the total number of elements divided by the amount that can
     * display on each page.
     */
    totalPages: number;
    /**
     * The index in the array of elements where we will start displaying elements in the array.
     */
    startIndex: number;
    /**
     * The index in the array of elements where we will stop displaying elements in the array.
     */
    endIndex: number;
    /**
     * When true, the previous button should display because there is a page before the current one that the user
     * can go to.
     */
    previousEnabled: boolean;
    /**
     * When true, the next button should display because there is a page after the current one that the user can
     * go to.
     */
    nextEnabled: boolean;
    /**
     * The total number of items that need to be paginated.
     */
    totalItems: number;
    /**
     * When true, the grid rows should be hidden so they can be rendered to the screen and measured while a
     * loading spinner displays. When false, the grid should display the rows.
     */
    shouldShow: boolean;
    /**
     * When true, we have retrieved the container element from the DOM and added a style directly to it so we can begin
     * measuring the height of the grid. When false, do not display anything in the grid yet since it will cause a flickering
     * due to DOM elements not being fully rendered.
     */
    isInitialized: boolean;
    /**
     * A function that acts as a callback ref on an element. When the ref is found because the element is now in the DOM, it forces
     * a re-render by updating a state to trigger logic that should run. This is specifically used to get an element that needs to be paginated
     * and starts the height measurement process.
     *
     * If we do not need to measure the height of the grid and dynamically determine how many elements can display on screen without a scrollbar,
     * then this does not need to be used.
     */
    pageRowCallbackRef: LegacyRef<HTMLDivElement> & GoMotoFunction<[HTMLElement | null], void>;
}

// #region Pagination Reducer
/**
 * The object used by the reducer to manage the hook's internal state and update which page and elements should be displayed.
 */
export type PaginationState = Pick<PaginationReturnProps, 'currentPage' | 'totalPages' | 'startIndex' | 'endIndex' | 'previousEnabled' | 'nextEnabled'>;

/**
 * The usePagination dispatch types used by our {@link ActionMap} type to distinguish what payload the
 * dispatch action will require.
 */
export enum PaginationDispatchTypes {
    SetPage = 'SET_PAGE',
    NextPage = 'NEXT_PAGE',
    PreviousPage = 'PREVIOUS_PAGE'
}

/**
 * An object mapping each dispatch type to the payload required for that dispatch action.
 */
export interface PaginationPayload {
    [PaginationDispatchTypes.SetPage]: number;
    [PaginationDispatchTypes.NextPage]: never;
    [PaginationDispatchTypes.PreviousPage]: never;
}

/**
 * The union type of dispatch actions that results from using {@link ActionMap}.
 */
export type PaginationActions = ActionMap<PaginationPayload>[keyof ActionMap<PaginationPayload>];
// #endregion